import * as React from 'react';

import { enumBillStatus } from '@rematter/frontend-lib-api-client';
import { Button, EntityDetails, Loading, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideMoreHorizontal } from 'lucide-react';

import {
  $billError,
  $billId,
  $isBillLoading,
  $markBillAsPaid,
  $remoteBillDetail,
  $tab,
  $voidBill,
} from '../../_state/billDetail.state';
import { financeRoutes } from '../../routes';
import { BillDetailsTab, BillRecord } from '../../types';
import { FinanceRootLayout } from '../_layouts/FinanceRootLayout';
import { BillStatusBadge } from '../_shared/BillStatusBadge';

import { BillTabInfo } from './BillTabInfo';
import { BillTabLineItems } from './BillTabLineItems';
import { BillTabPayments } from './BillTabPayments';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ BILL DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const BillDetailView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // Atoms
  const billId = useAtomValue($billId);
  const queryResult = useAtomValue($remoteBillDetail);
  const bill = (queryResult?.data as { bill: BillRecord | null } | undefined)
    ?.bill;
  const refetch = queryResult?.refetch;
  const isLoading = useAtomValue($isBillLoading);
  const error = useAtomValue($billError);
  const [tab, setTab] = useAtom($tab);
  const voidBill = useSetAtom($voidBill);
  const markBillAsPaid = useSetAtom($markBillAsPaid);

  // Other hooks
  const toast = useToast();

  const activeTab = (tab || 'info') as BillDetailsTab;

  // Memoized values
  const isDisabled = React.useMemo(
    () =>
      bill?.status === enumBillStatus.Paid ||
      bill?.status === enumBillStatus.Voided,
    [bill?.status],
  );

  const infoTabContent = React.useMemo(
    () => () => <BillTabInfo disabled={isDisabled} />,
    [isDisabled],
  );

  const lineItemsTabContent = React.useMemo(
    () => () => <BillTabLineItems disabled={isDisabled} />,
    [isDisabled],
  );

  const paymentsTabContent = React.useMemo(
    () => () => <BillTabPayments disabled={isDisabled} />,
    [isDisabled],
  );

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(financeRoutes.bills);
  }, [navigate]);

  const handleVoid = React.useCallback(async () => {
    if (!billId) return;
    try {
      await voidBill({ id: billId });
      toast.success(t.messages.bills.voidSuccess);
      refetch?.();
    } catch (err) {
      toast.error(
        t.messages.bills.voidError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    }
  }, [billId, voidBill, toast, refetch, t]);

  const handleMarkAsPaid = React.useCallback(async () => {
    if (!billId) return;
    try {
      await markBillAsPaid({ id: billId });
      toast.success(t.messages.bills.markAsPaidSuccess);
      refetch?.();
    } catch (err) {
      toast.error(
        t.messages.bills.markAsPaidError(
          err instanceof Error ? err.message : t.messages.common.unknownError,
        ),
      );
    }
  }, [billId, markBillAsPaid, toast, refetch, t]);

  const menuItems = React.useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      onAction: () => void;
      disabled?: boolean;
    }> = [];

    if (bill?.status === enumBillStatus.Pending) {
      items.push({
        id: 'markAsPaid',
        label: t.bills.detail.actions.markAsPaid,
        onAction: handleMarkAsPaid,
      });
    }

    if (!isDisabled) {
      items.push({
        id: 'void',
        label: t.bills.detail.actions.void,
        onAction: handleVoid,
      });
    }

    return items;
  }, [bill?.status, isDisabled, handleVoid, handleMarkAsPaid, t]);

  if (error) {
    return (
      <FinanceRootLayout>
        <div className="p-4 text-failure">
          {t.messages.bills.loadError(error.message)}
        </div>
      </FinanceRootLayout>
    );
  }

  if (isLoading || !bill) {
    return (
      <FinanceRootLayout>
        <div className="p-4">
          <Loading />
        </div>
      </FinanceRootLayout>
    );
  }

  return (
    <FinanceRootLayout className="@BillDetailView">
      <EntityDetails
        onChangeTab={(newTab) => setTab(newTab as BillDetailsTab)}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={`Bill #${bill.documentNumber}`}
        subtitle={bill.accountName}
        badges={<BillStatusBadge status={bill.status} />}
        headerActions={
          menuItems.length > 0 && (
            <Menu
              trigger={
                <Button
                  icon={<LucideMoreHorizontal className="size-4" />}
                  variant="outline"
                  className="w-8"
                />
              }
              items={menuItems}
            />
          )
        }
        tabs={{
          info: {
            title: t.bills.detail.tabs.info,
            content: infoTabContent,
          },
          'line-items': {
            title: t.bills.detail.tabs.lineItems,
            content: lineItemsTabContent,
          },
          payments: {
            title: t.bills.detail.tabs.payments,
            content: paymentsTabContent,
          },
        }}
      />
    </FinanceRootLayout>
  );
};
